import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import styles from './ForNetworkers.module.css';

export default function ForNetworkers() {
  return (
    <>
      <Helmet>
        <title>For Networkers | Your Personal Relationship OS | MyPeopleDatabase</title>
        <meta
          name="description"
          content="MyPeopleDatabase is the ultimate tool for networkers. Track every connection, remember important details, organize by events and industries, and never let a valuable relationship go cold."
        />
        <meta
          name="keywords"
          content="networking tool, relationship management, contact organization, business networking, CRM for networkers, networking events, follow-up system"
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Your Personal Relationship OS</h1>
            <p className={styles.heroSubtitle}>
              Stop losing connections. Start building lasting relationships.
            </p>
            <p className={styles.heroDescription}>
              MyPeopleDatabase helps serious networkers track every connection, remember crucial
              details, and turn casual meetings into valuable long-term relationships.
            </p>
            <div className={styles.heroCta}>
              <Link to="/buy" className={styles.primaryButton}>
                Get Started - $49.99
              </Link>
              <Link to="/download" className={styles.secondaryButton}>
                Try Free Demo
              </Link>
            </div>
            <div className={styles.trustBar}>
              <span className={styles.trustItem}>🔒 Your data stays on your device</span>
              <span className={styles.trustItem}>🚫 No ads, no data mining</span>
              <span className={styles.trustItem}>💳 Secure PayPal checkout</span>
              <span className={styles.trustItem}>💾 Free download to try first</span>
            </div>
          </div>
        </div>

        <div className={styles.problemSection}>
          <h2 className={styles.sectionTitle}>The Networker's Dilemma</h2>
          <div className={styles.problems}>
            <div className={styles.problem}>
              <div className={styles.problemIcon}>😰</div>
              <h3>You meet amazing people...</h3>
              <p>But forget to follow up and the connection goes cold</p>
            </div>
            <div className={styles.problem}>
              <div className={styles.problemIcon}>🤔</div>
              <h3>You can't remember...</h3>
              <p>Where you met someone or what you talked about</p>
            </div>
            <div className={styles.problem}>
              <div className={styles.problemIcon}>📱</div>
              <h3>Your contacts are scattered...</h3>
              <p>Across business cards, LinkedIn, phone, email, and sticky notes</p>
            </div>
            <div className={styles.problem}>
              <div className={styles.problemIcon}>⏰</div>
              <h3>You want to nurture relationships...</h3>
              <p>But don't have a system to stay organized and consistent</p>
            </div>
          </div>
        </div>

        <div className={styles.solutionSection}>
          <h2 className={styles.sectionTitle}>Built for How You Network</h2>
          <p className={styles.sectionIntro}>
            MyPeopleDatabase gives you the tools professional networkers need to build and maintain
            a powerful network.
          </p>

          <div className={styles.features}>
            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>🏷️</span>
                <h3>Smart Group Organization</h3>
              </div>
              <p className={styles.featureDescription}>
                Create custom groups for every networking context. Organize contacts by:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Events:</strong> "Tech Summit 2026", "Chamber Mixer March", "BNI Chapter"
                </li>
                <li>
                  <strong>Industries:</strong> "Real Estate", "Tech Founders", "Marketing Pros"
                </li>
                <li>
                  <strong>Relationship Stage:</strong> "Hot Leads", "Need Follow-up", "Long-term
                  Partners"
                </li>
                <li>
                  <strong>Geography:</strong> "NYC Contacts", "Remote Collaborators", "Bay Area
                  Network"
                </li>
                <li>
                  <strong>Potential:</strong> "Referral Partners", "Mentors", "Potential Clients"
                </li>
              </ul>
              <p className={styles.featureTip}>
                💡 <strong>Pro Tip:</strong> People can belong to multiple groups. Tag someone as
                both "Tech Summit 2026" AND "Potential Clients" for easy filtering.
              </p>
            </div>

            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>📝</span>
                <h3>Detailed Notes & Context</h3>
              </div>
              <p className={styles.featureDescription}>
                Never forget important details about your connections:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Meeting Notes:</strong> What you discussed, their pain points, interests
                </li>
                <li>
                  <strong>Personal Details:</strong> Kids' names, hobbies, upcoming vacations
                </li>
                <li>
                  <strong>Business Context:</strong> Their goals, challenges, decision-making
                  timeline
                </li>
                <li>
                  <strong>Follow-up Actions:</strong> What you promised to send or introduce
                </li>
                <li>
                  <strong>Conversation History:</strong> Track every interaction over time
                </li>
              </ul>
              <p className={styles.featureTip}>
                💡 <strong>Pro Tip:</strong> Add notes immediately after meeting someone. Future you
                will thank present you.
              </p>
            </div>

            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>🔍</span>
                <h3>Powerful Search & Filtering</h3>
              </div>
              <p className={styles.featureDescription}>
                Find exactly who you need, when you need them:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Search by Name:</strong> Instant results as you type
                </li>
                <li>
                  <strong>Filter by Group:</strong> "Show me everyone from the Tech Summit"
                </li>
                <li>
                  <strong>Search Notes:</strong> "Who mentioned they need a web developer?"
                </li>
                <li>
                  <strong>Multi-Group Filtering:</strong> "Tech founders in NYC who I met this
                  year"
                </li>
                <li>
                  <strong>Quick Access:</strong> Star your most important contacts for instant
                  access
                </li>
              </ul>
            </div>

            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>🔗</span>
                <h3>Relationship Mapping</h3>
              </div>
              <p className={styles.featureDescription}>
                Track how people connect to each other in your network:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Mutual Connections:</strong> "How do I know this person? Through Sarah."
                </li>
                <li>
                  <strong>Introduction Tracking:</strong> Remember who introduced you to whom
                </li>
                <li>
                  <strong>Network Mapping:</strong> Visualize clusters of connected people
                </li>
                <li>
                  <strong>Referral Paths:</strong> Identify who can introduce you to key contacts
                </li>
              </ul>
            </div>

            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>📅</span>
                <h3>Never Miss Important Dates</h3>
              </div>
              <p className={styles.featureDescription}>
                Build deeper relationships by remembering what matters:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Birthdays:</strong> Send personalized messages that stand out
                </li>
                <li>
                  <strong>Work Anniversaries:</strong> Celebrate their milestones
                </li>
                <li>
                  <strong>Follow-up Reminders:</strong> "Check in with John in 2 weeks"
                </li>
                <li>
                  <strong>Calendar Integration:</strong> See upcoming dates at a glance
                </li>
              </ul>
            </div>

            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>🔒</span>
                <h3>Own Your Network Data</h3>
              </div>
              <p className={styles.featureDescription}>
                Unlike LinkedIn or other platforms, YOU own your data:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Complete Control:</strong> Your network isn't held hostage by a platform
                </li>
                <li>
                  <strong>Privacy First:</strong> No ads, no data mining, no selling your
                  information
                </li>
                <li>
                  <strong>Export Anytime:</strong> Your data is always accessible and portable
                </li>
                <li>
                  <strong>Offline Access:</strong> Desktop app works without internet
                </li>
              </ul>
            </div>

            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>⚡</span>
                <h3>Quick Capture & Updates</h3>
              </div>
              <p className={styles.featureDescription}>
                Designed for speed when you're networking on the go:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Fast Entry:</strong> Add new contacts in seconds
                </li>
                <li>
                  <strong>Bulk Import:</strong> Import from business cards, LinkedIn, phone
                </li>
                <li>
                  <strong>Quick Edit:</strong> Update notes and groups with minimal clicks
                </li>
                <li>
                  <strong>Batch Operations:</strong> Add multiple people to a group at once
                </li>
              </ul>
            </div>

            <div className={styles.feature}>
              <div className={styles.featureHeader}>
                <span className={styles.featureIcon}>🤖</span>
                <h3>AI-Powered Insights (Optional)</h3>
              </div>
              <p className={styles.featureDescription}>
                Use AI to work smarter with your network:
              </p>
              <ul className={styles.featureList}>
                <li>
                  <strong>Smart Search:</strong> "Find everyone in tech who I met in Q1"
                </li>
                <li>
                  <strong>Relationship Summaries:</strong> Quick context before reaching out
                </li>
                <li>
                  <strong>Follow-up Suggestions:</strong> AI reminds you who to reconnect with
                </li>
                <li>
                  <strong>Local Processing:</strong> AI runs on your machine - your data stays
                  private
                </li>
              </ul>
              <p className={styles.featureTip}>
                <Link to="/features-ai" className={styles.featureLink}>
                  Learn more about AI features →
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className={styles.useCasesSection}>
          <h2 className={styles.sectionTitle}>Real Networkers, Real Results</h2>
          <div className={styles.useCases}>
            <div className={styles.useCase}>
              <h3>🎯 The Conference Networker</h3>
              <p>
                <strong>Challenge:</strong> Attends 10+ conferences per year, meets 100+ people,
                struggles to follow up effectively.
              </p>
              <p>
                <strong>Solution:</strong> Creates a group for each conference. During the event,
                quickly adds contacts with notes about their interests and pain points. After the
                conference, filters by that group to send personalized follow-ups.
              </p>
              <p className={styles.result}>
                <strong>Result:</strong> Converts 30% more connections into meaningful
                relationships.
              </p>
            </div>

            <div className={styles.useCase}>
              <h3>💼 The Business Development Pro</h3>
              <p>
                <strong>Challenge:</strong> Manages relationships with 500+ contacts across
                different industries, needs to track referral sources and partnership opportunities.
              </p>
              <p>
                <strong>Solution:</strong> Organizes contacts by industry, relationship stage, and
                referral potential. Uses notes to track conversation history and business
                opportunities. Sets reminders for quarterly check-ins.
              </p>
              <p className={styles.result}>
                <strong>Result:</strong> Never misses a follow-up, closes 40% more deals through
                warm introductions.
              </p>
            </div>

            <div className={styles.useCase}>
              <h3>🌟 The Community Builder</h3>
              <p>
                <strong>Challenge:</strong> Runs multiple networking groups, needs to connect people
                with each other, track who knows whom.
              </p>
              <p>
                <strong>Solution:</strong> Maps relationships between members, tracks skills and
                interests in notes, creates groups for different communities. Uses search to find
                perfect matches for introductions.
              </p>
              <p className={styles.result}>
                <strong>Result:</strong> Becomes the go-to connector, creates massive value by
                facilitating strategic introductions.
              </p>
            </div>

            <div className={styles.useCase}>
              <h3>🚀 The Entrepreneur</h3>
              <p>
                <strong>Challenge:</strong> Building a startup, needs investors, advisors, early
                customers, and team members from their network.
              </p>
              <p>
                <strong>Solution:</strong> Tags contacts by potential role (investor, advisor,
                customer, hire). Tracks conversation history and interest level. Uses notes to
                remember specific asks and offers.
              </p>
              <p className={styles.result}>
                <strong>Result:</strong> Builds a strong support network, raises funding faster,
                finds perfect co-founder through a warm introduction.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.comparisonSection}>
          <h2 className={styles.sectionTitle}>Why Not Just Use LinkedIn?</h2>
          <div className={styles.comparison}>
            <div className={styles.comparisonColumn}>
              <h3 className={styles.comparisonHeader}>LinkedIn</h3>
              <ul className={styles.comparisonList}>
                <li className={styles.con}>Limited notes and context</li>
                <li className={styles.con}>No custom organization</li>
                <li className={styles.con}>Platform owns your network</li>
                <li className={styles.con}>Requires internet connection</li>
                <li className={styles.con}>Distracting feed and notifications</li>
                <li className={styles.con}>Can't track offline connections</li>
                <li className={styles.con}>Limited search capabilities</li>
              </ul>
            </div>
            <div className={styles.comparisonColumn}>
              <h3 className={styles.comparisonHeader}>MyPeopleDatabase</h3>
              <ul className={styles.comparisonList}>
                <li className={styles.pro}>Unlimited detailed notes</li>
                <li className={styles.pro}>Fully customizable groups</li>
                <li className={styles.pro}>You own 100% of your data</li>
                <li className={styles.pro}>Works offline</li>
                <li className={styles.pro}>Focused, distraction-free</li>
                <li className={styles.pro}>Track anyone, anywhere</li>
                <li className={styles.pro}>Powerful search and filtering</li>
              </ul>
            </div>
          </div>
          <p className={styles.comparisonNote}>
            <strong>Use both:</strong> LinkedIn for public presence and discovery, MyPeopleDatabase
            for serious relationship management and private notes.
          </p>
        </div>

        <div className={styles.pricingSection}>
          <h2 className={styles.sectionTitle}>Simple, Affordable Pricing</h2>
          <div className={styles.pricingCards}>
            <div className={styles.pricingCard}>
              <h3>One-Time Purchase</h3>
              <div className={styles.price}>$49.99</div>
              <p className={styles.priceDescription}>Pay once, own forever</p>
              <ul className={styles.pricingFeatures}>
                <li>Desktop app for Mac/Windows/Linux</li>
                <li>Unlimited contacts</li>
                <li>Unlimited groups</li>
                <li>All features included</li>
                <li>Free updates</li>
                <li>No recurring fees</li>
              </ul>
              <Link to="/buy" className={styles.pricingButton}>
                Buy Now
              </Link>
            </div>
            <div className={styles.pricingCard}>
              <h3>Annual Subscription</h3>
              <div className={styles.price}>$39.99/year</div>
              <p className={styles.priceDescription}>Lower upfront cost</p>
              <ul className={styles.pricingFeatures}>
                <li>Desktop app for Mac/Windows/Linux</li>
                <li>Unlimited contacts</li>
                <li>Unlimited groups</li>
                <li>All features included</li>
                <li>Priority support</li>
                <li>Cancel anytime</li>
              </ul>
              <Link to="/buy" className={styles.pricingButton}>
                Subscribe
              </Link>
            </div>
          </div>
          <p className={styles.pricingNote}>
            💰 <strong>ROI:</strong> One good connection pays for itself. Most networkers see value
            within the first week.
          </p>
        </div>

        <div className={styles.ctaSection}>
          <h2 className={styles.ctaTitle}>Stop Losing Connections. Start Building Relationships.</h2>
          <p className={styles.ctaDescription}>
            Turn every conversation into a lasting relationship. Download free to try it first, or
            get lifetime access today.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/buy" className={styles.ctaPrimaryButton}>
              Get Started - $49.99
            </Link>
            <Link to="/download" className={styles.ctaSecondaryButton}>
              Try Free Demo
            </Link>
          </div>
          <p className={styles.ctaNote}>
            Questions? <Link to="/contact">Contact us</Link> or check out our <Link to="/faq">FAQ</Link>
          </p>
        </div>
      </div>
    </>
  );
}
